import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import {
  FiGrid, FiClipboard, FiUsers, FiMonitor,
  FiSettings, FiPlusCircle, FiGlobe, FiTool, FiAlertCircle,
  FiEdit3, FiLogOut, FiChevronDown,
} from 'react-icons/fi';
import Notificaciones from './Notificaciones';
import FotoPerfil from './ui/FotoPerfil';
import BASE_URL from '../config/config';
import '../css/sidebar.css';
import { DEPTO_LABELS_CORTO } from '../constants/usuarios';

// Navegación por rol: cada entrada es un link directo o un grupo con submenú.
export const buildNav = (role) => {
  const comunes = [
    { label: 'Mis servicios', path: '/dashboard/servicios', icon: FiTool },
    { label: 'Reportar un error', href: 'https://agenciaadhoc.com/reportes', icon: FiAlertCircle },
  ];

  if (role === 'admin') {
    return [
      { label: 'Panel', path: '/dashboard', icon: FiGrid },
      {
        label: 'Gestión', icon: FiClipboard, submenu: [
          { label: 'Notificaciones', path: '/dashboard/notificaciones' },
          { label: 'Proyectos', path: '/dashboard/gestion-proyectos' },
          { label: 'Servicios', path: '/dashboard/gestion-servicios' },
          { label: 'Tickets', path: '/dashboard/tickets' },
          { label: 'Usuarios', path: '/dashboard/usuarios' },
        ]
      },
      {
        label: 'Material', icon: FiMonitor, submenu: [
          { label: 'Equipos', path: '/dashboard/equipos' },
          { label: 'Periféricos', path: '/dashboard/perifericos' },
        ]
      },
      { label: 'Ajustes del sistema', path: '/dashboard/ajustes-del-sistema', icon: FiSettings },
      // { label: 'Hoja de ruta', path: '/dashboard/roadmap', icon: FiMap },
      ...comunes,
    ];
  }

  if (role === 'tecnico') {
    return [
      { label: 'Panel', path: '/dashboard', icon: FiGrid },
      { label: 'Gestión de tickets', path: '/dashboard/tickets', icon: FiClipboard },
      { label: 'Gestión de usuarios', path: '/dashboard/usuarios', icon: FiUsers },
      // { label: 'Hoja de ruta', path: '/dashboard/roadmap', icon: FiMap },
      ...comunes,
    ];
  }

  // Cliente
  return [
    { label: 'Mis tickets', path: '/dashboard', icon: FiGrid },
    { label: 'Nuevo ticket', path: '/nuevo-ticket', icon: FiPlusCircle },
    { label: 'Mis proyectos', path: '/dashboard/proyectos', icon: FiGlobe },
    // { label: 'Hoja de ruta', path: '/dashboard/roadmap', icon: FiMap },
    ...comunes,
  ];
};

/**
 * Panel lateral (nav por rol + perfil + logout). Lo comparten la Sidebar de
 * las rutas de app y el Header público, así el menú lateral responsive es
 * idéntico en el inicio y en el dashboard.
 *
 * @param open               panel desplegado (móvil)
 * @param onClose            cierre tras navegar / logout
 * @param soloMovil          true en el Header público: el panel no existe en escritorio
 * @param conNotificaciones  campana en la cabecera del panel. Solo la usa la
 *                           sidebar fija de escritorio; en responsive la
 *                           campana vive en la barra superior / el header.
 */
const MenuLateral = ({ open = false, onClose = () => {}, soloMovil = false, conNotificaciones = false }) => {
  const [token, setToken] = useState(localStorage.getItem('AuthToken'));
  const tokenInfo = token ? jwtDecode(token) : {};
  const location = useLocation();
  const navigate = useNavigate();

  const role = tokenInfo.role;
  const esCliente = role !== 'admin' && role !== 'tecnico';
  const esStaff = !esCliente;

  const [usuarioInfo, setUsuarioInfo] = useState(null);
  const [ticketsUrgentesSinAsignar, setTicketsUrgentesSinAsignar] = useState(0);
  const [openGroups, setOpenGroups] = useState({});
  const groupsInitRef = useRef(false);

  const nav = buildNav(role);

  const displayName = [tokenInfo.nombre, tokenInfo.apellidos].filter(Boolean).join(' ') || tokenInfo.usuario || 'Usuario';

  useEffect(() => {
    const onTokenChanged = () => setToken(localStorage.getItem('AuthToken'));
    window.addEventListener('auth-token-changed', onTokenChanged);
    return () => window.removeEventListener('auth-token-changed', onTokenChanged);
  }, []);

  useEffect(() => {
    if (!esStaff || !tokenInfo.usuario) return;
    const fetchUsuarioInfo = async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/usuario/info/${tokenInfo.usuario}`, {
          headers: { Authorization: token },
        });
        setUsuarioInfo(resp.data.usuario);
      } catch (err) {
        console.error('Error al obtener info de usuario:', err);
      }
    };
    fetchUsuarioInfo();
  }, [esStaff, tokenInfo.usuario, token]);

  useEffect(() => {
    if (!esStaff) return;
    const fetchTicketsUrgentes = async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/tickets/total/?page=1&limit=1000`, {
          headers: { Authorization: token },
        });
        const tickets = resp.data.tickets || [];
        const count = tickets.filter((t) => t.prioridad === 'urgente' && t.usuario_encargado === 'sin_asignar').length;
        setTicketsUrgentesSinAsignar(count);
      } catch (err) {
        console.error('Error al obtener tickets urgentes sin asignar:', err);
      }
    };
    fetchTicketsUrgentes();
  }, [esStaff, token]);

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (group) => group.submenu.some((i) => location.pathname === i.path);

  // Abre automáticamente el grupo que contiene la ruta activa (solo una vez al montar).
  useEffect(() => {
    if (groupsInitRef.current) return;
    groupsInitRef.current = true;
    const initial = {};
    nav.forEach((item) => {
      if (item.submenu && isGroupActive(item)) initial[item.label] = true;
    });
    setOpenGroups(initial);
  }, []);

  const handleLinkClick = () => {
    window.scrollTo({ top: 0 });
    onClose();
  };

  const handleLogout = () => {
    localStorage.removeItem('AuthToken');
    navigate('/iniciar-sesion', { replace: true });
    window.location.reload();
  };

  const toggleGroup = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const renderNavItem = (item) => {
    if (item.href) {
      return (
        <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" className="sidebar-link">
          <item.icon /><span>{item.label}</span>
        </a>
      );
    }

    if (item.submenu) {
      const abierto = !!openGroups[item.label];
      return (
        <div key={item.label} className={`sidebar-group ${abierto ? 'open' : ''} ${isGroupActive(item) ? 'active' : ''}`}>
          <button type="button" className="sidebar-link sidebar-group-btn" onClick={() => toggleGroup(item.label)} aria-expanded={abierto}>
            <item.icon /><span>{item.label}</span>
            <FiChevronDown className="sidebar-chevron" />
          </button>
          {abierto && (
            <div className="sidebar-submenu">
              {item.submenu.map((sub) => (
                <Link
                  key={sub.path}
                  to={sub.path}
                  onClick={handleLinkClick}
                  className={`sidebar-sublink ${isActive(sub.path) ? 'active' : ''}`}
                >
                  {sub.label}
                  {sub.path === '/dashboard/tickets' && ticketsUrgentesSinAsignar > 0 && (
                    <span className="sidebar-alert-badge" title="Tickets urgentes sin asignar">{ticketsUrgentesSinAsignar}</span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={handleLinkClick}
        className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
      >
        <item.icon /><span>{item.label}</span>
        {item.path === '/dashboard/tickets' && ticketsUrgentesSinAsignar > 0 && (
          <span className="sidebar-alert-badge" title="Tickets urgentes sin asignar">{ticketsUrgentesSinAsignar}</span>
        )}
      </Link>
    );
  };

  return (
    <aside className={`app-sidebar ${open ? 'open' : ''} ${soloMovil ? 'solo-movil' : ''}`}>
      <div className="sidebar-header">
        <Link to="/" className="sidebar-logo-link" onClick={handleLinkClick}>
          <img className="sidebar-logo" src="/images/logoadhoc.svg" alt="Logo ADHOC" />
        </Link>
        {conNotificaciones && <Notificaciones />}
      </div>

      <nav className="sidebar-nav">
        {nav.map(renderNavItem)}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-profile">
          <FotoPerfil editable={false} />
          <div className="sidebar-profile-info">
            <h3 className="sidebar-profile-name">{displayName}</h3>
            {tokenInfo.usuario && <p className="sidebar-profile-username">@{tokenInfo.usuario}</p>}
          </div>
          <div className="sidebar-profile-actions">
            <button type="button" className="sidebar-action-btn sidebar-action-edit" onClick={() => { navigate('/dashboard/editar-perfil'); handleLinkClick(); }} title="Editar perfil" aria-label="Editar perfil">
              <FiEdit3 />
            </button>
            <button type="button" className="sidebar-action-btn sidebar-logout" onClick={handleLogout} title="Salir" aria-label="Salir">
              <FiLogOut />
            </button>
          </div>
        </div>

        {/* Fuera de la fila del avatar: así dispone del ancho completo y el
            departamento no se corta contra los botones */}
        {esStaff && usuarioInfo && (
          <p
            className="sidebar-profile-meta"
            title={`${usuarioInfo.role === 'admin' ? 'Administrador' : 'Técnico'} · ${DEPTO_LABELS_CORTO[usuarioInfo.dpto_tecnico] || 'Todos los dptos.'}`}
          >
            <span className={`sidebar-role-punto ${usuarioInfo.role === 'admin' ? 'admin' : 'tecnico'}`} aria-hidden="true" />
            <span className="sidebar-role-texto">
              {usuarioInfo.role === 'admin' ? 'Administrador' : 'Técnico'}
            </span>
            <span className="sidebar-meta-sep" aria-hidden="true">·</span>
            <span className="sidebar-dpto-texto">
              {DEPTO_LABELS_CORTO[usuarioInfo.dpto_tecnico] || 'Todos los dptos.'}
            </span>
          </p>
        )}
      </div>
    </aside>
  );
};

export default MenuLateral;
